import React, { useState } from "react";
import { markupExample, ratioExample } from "./helpers";

/**
 * Builds the editable draft from a saved configuration.
 *
 * @param {object} config - The rates currently in force.
 * @returns {object} The draft, keyed by the field names the API expects.
 */
const draftOf = (config) => ({
  markup_percent: config.markupPercent,
  credits_per_usd: config.creditsPerUsd,
  fx_max_age_minutes: String(config.fxMaxAgeMinutes),
});

/**
 * Editor for the global AI credit rates.
 *
 * @param {object} props - Component props.
 * @param {object} props.config - The rates currently in force.
 * @param {Function} props.onSave - Called with the changed fields.
 * @param {boolean} props.saving - Whether a save is in flight.
 * @returns {JSX.Element} The rates card.
 *
 * @remarks
 * Holds its own draft rather than writing through to the parent on every
 * keystroke, so a half-typed rate is never a rate. The draft is seeded from the
 * saved config and reset against it, which also makes Cancel meaningful.
 */
const RatesCard = ({ config, onSave, saving }) => {
  const [draft, setDraft] = useState(draftOf(config));

  const saved = draftOf(config);
  const dirty = Object.keys(saved).some((key) => draft[key] !== saved[key]);

  const change = (field) => (event) =>
    setDraft({ ...draft, [field]: event.target.value });

  const submit = (event) => {
    event.preventDefault();
    onSave(draft);
  };

  return (
    <form className="aicr-card" onSubmit={submit}>
      <h2>Rates</h2>
      <p className="aicr-card-sub">
        Markup is applied when a customer consumes AI. Saving records a new
        configuration rather than editing this one, so past charges can still be
        explained at the rate they were billed under.
      </p>

      <div className="aicr-grid">
        <div className="aicr-field">
          <label htmlFor="aicr-markup">Markup on provider cost (%)</label>
          <input
            id="aicr-markup"
            type="text"
            inputMode="decimal"
            value={draft.markup_percent}
            onChange={change("markup_percent")}
          />
          <span className="aicr-hint">{markupExample(draft.markup_percent)}</span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-ratio">Credits per US dollar</label>
          <input
            id="aicr-ratio"
            type="text"
            inputMode="decimal"
            value={draft.credits_per_usd}
            onChange={change("credits_per_usd")}
          />
          <span className="aicr-hint">{ratioExample(draft.credits_per_usd)}</span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-fx">Exchange rate freshness (minutes)</label>
          <input
            id="aicr-fx"
            type="text"
            inputMode="numeric"
            value={draft.fx_max_age_minutes}
            onChange={change("fx_max_age_minutes")}
          />
          <span className="aicr-hint">
            A purchase is refused rather than guessed at when the cached USD-INR
            rate is older than this.
          </span>
        </div>
      </div>

      <div className="aicr-actions">
        <button
          type="submit"
          className="aicr-btn aicr-btn-primary"
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save rates"}
        </button>
        <button
          type="button"
          className="aicr-btn aicr-btn-secondary"
          onClick={() => setDraft(draftOf(config))}
          disabled={!dirty || saving}
        >
          Cancel
        </button>
        {!dirty && <span className="aicr-muted">No unsaved changes.</span>}
      </div>
    </form>
  );
};

export default RatesCard;
